"use client";

/**
 * Client island for the LEFT column of the tour-detail page, BELOW the
 * SSR'd hero/breadcrumb/pills/title shell rendered by the RSC page.
 *
 * Owns the interactive state for:
 *  - tabbed "Tout savoir sur l'expérience" panel (activeTab)
 *  - "see more / see less" description toggle
 *  - viewport-driven `isDesktop` (used to set the desc word-limit)
 *  - IntersectionObserver-deferred Reviews mounting
 *
 * Reads tour data from Redux (already hydrated synchronously by
 * TourLayoutClient before this component mounts), so we don't need to
 * thread the full tour object through props. Only the static description
 * + a few labels come in as props so the server-rendered description
 * intro matches what hydration produces (avoids a flash on `tourDescription`).
 */
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { StarIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@/app/lib/hooks";
import ItinerariesH from "./ItinerariesH";
import CmsEntry from "@/components/TourThingsToKnow/CmsEntry";

// Reviews chunk only fetches when the user scrolls within ~600 px of the
// section — saves ~140 KB of JS + the /reviews API call from the
// critical path on mobile.
const Reviews = dynamic(() => import("./Reviews"), {
  ssr: false,
  loading: () => <div className="min-h-[200px]" aria-hidden="true" />,
});

interface Props {
  /** Pre-rendered, server-truncated description body for the initial paint
      (60 words on mobile, 150 on desktop). Client renders the full text
      once `showFullContent` is true. */
  initialDescription: string;
  fullDescription: string;
  fullDescriptionDesktop: string;
}

// Inline SVG helper — copies the one in page.tsx so this client component
// has zero RSC->client crossings.
const SvgIcon: FC<{ size?: number; children: React.ReactNode }> = ({
  size = 18,
  children,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

type TileDef = { id: string; label: string; sub: string; path: React.ReactNode };

const TourLeftBody: FC<Props> = ({
  initialDescription,
  fullDescription,
  fullDescriptionDesktop,
}) => {
  const { t } = useTranslation("guydeez");

  // Redux-backed selectors — hydrated synchronously by TourLayoutClient.
  const tour = useAppSelector((s) => s.tour.tourDetails);
  const reviews = useAppSelector((s) => s.reviews);
  const entryHighlights = useAppSelector(
    (s) => s.tour.tourDetails?.thingsToKnow?.highlights
  );
  const entryWhatsIncluded = useAppSelector(
    (s) => s.tour.tourDetails?.thingsToKnow?.whatsIncluded
  );
  const entryWhatsNotIncluded = useAppSelector(
    (s) => s.tour.tourDetails?.thingsToKnow?.whatsNotIncluded
  );
  const entryImportantInformation = useAppSelector(
    (s) => s.tour.tourDetails?.thingsToKnow?.importantInformation
  );
  const entryCancellation = useAppSelector(
    (s) => s.tour.tourDetails?.thingsToKnow?.cancellation
  );
  const entryAccessibility = useAppSelector(
    (s) => s.tour.tourDetails?.thingsToKnow?.accessibility
  );
  const entryMeetingPoint = useAppSelector(
    (s) => s.tour.tourDetails?.thingsToKnow?.meetingPoint
  );

  // Viewport state for the "see more" word limit.
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Tab + description state.
  const [activeTab, setActiveTab] = useState("itinerary");
  const [showFullContent, setShowFullContent] = useState(false);

  // Reviews IntersectionObserver gate.
  const reviewsRef = useRef<HTMLDivElement>(null);
  const [reviewsMounted, setReviewsMounted] = useState(false);
  useEffect(() => {
    if (reviewsMounted) return;
    const el = reviewsRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setReviewsMounted(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setReviewsMounted(true);
          obs.disconnect();
        }
      },
      { rootMargin: "600px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [reviewsMounted]);

  const handleScrollToReviews = () => {
    reviewsRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const ratingValue = reviews?.tourRating || 0;
  const totalReviews = reviews?.tourTotalReviews || 0;
  const showRating = ratingValue > 2.5;

  // Resolve the city display name for the "rating · ◉ City" row. We
  // duplicate the RSC's titleCase fallback chain here (against Redux).
  const cityFromRedux = useAppSelector((s) => s.cities.currentCityData);
  const cityName = useMemo(() => {
    const raw =
      (cityFromRedux as any)?.entry?.data?.[0]?.title ||
      (tour as any)?.city?.entry?.data?.[0]?.title ||
      (cityFromRedux as any)?.nameTranslated ||
      (tour as any)?.city?.nameTranslated ||
      cityFromRedux?.name ||
      tour?.city?.name ||
      "";
    if (!raw) return "";
    return raw
      .split(" ")
      .map((w: string) =>
        w
          .split("-")
          .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : ""))
          .join("-")
      )
      .join(" ");
  }, [cityFromRedux, tour]);

  const tabs: TileDef[] = [
    {
      id: "itinerary",
      label: t("tour_itinerary"),
      sub: t("tour_itinerary_sub", { defaultValue: "Le déroulé heure par heure" }) as string,
      path: <path d="M4 6h13M4 12h13M4 18h9" />,
    },
    {
      id: "highlights",
      label: t("tour_highlights"),
      sub: t("tour_highlights_sub", { defaultValue: "Ce qui rend ce tour unique" }) as string,
      path: <path d="M12 2l2.4 5.5L20 8l-4.5 3.8L17 18l-5-3-5 3 1.5-6.2L4 8l5.6-.5L12 2z" />,
    },
    {
      id: "included",
      label: t("tour_included"),
      sub: t("tour_included_sub", { defaultValue: "Tout ce qui est compris" }) as string,
      path: <path d="M5 12l4 4L19 6" />,
    },
    {
      id: "not-included",
      label: t("tour_not_included"),
      sub: t("tour_not_included_sub", { defaultValue: "À prévoir séparément" }) as string,
      path: (
        <>
          <path d="M6 6l12 12" />
          <path d="M18 6L6 18" />
        </>
      ),
    },
    {
      id: "important-information",
      label: t("tour_important_information"),
      sub: t("tour_important_information_sub", {
        defaultValue: "Ce qu'il faut préparer",
      }) as string,
      path: (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v5" />
          <circle cx="12" cy="16.5" r="0.6" fill="currentColor" stroke="none" />
        </>
      ),
    },
    {
      id: "meeting-point",
      label: t("tour_meeting_point"),
      sub: t("tour_meeting_point_sub", { defaultValue: "Où retrouver votre guide" }) as string,
      path: (
        <>
          <path d="M12 22s-7-7.5-7-13a7 7 0 0 1 14 0c0 5.5-7 13-7 13z" />
          <circle cx="12" cy="9" r="2.5" />
        </>
      ),
    },
    {
      id: "accessibility",
      label: t("tour_accessibility"),
      sub: t("tour_accessibility_sub", { defaultValue: "PMR, enfants, conditions" }) as string,
      path: (
        <>
          <circle cx="12" cy="4" r="2" />
          <path d="M9 9h6l1 5h3" />
          <circle cx="10" cy="17" r="3.5" />
        </>
      ),
    },
    {
      id: "cancellation",
      label: t("tour_cancellation"),
      sub: t("tour_cancellation_sub", { defaultValue: "Gratuite jusqu'à 24h avant" }) as string,
      path: (
        <>
          <rect x="4" y="5" width="16" height="15" rx="2" />
          <path d="M4 10h16M9 3v4M15 3v4" />
        </>
      ),
    },
  ];

  const renderStars = (rating: number) => {
    const rounded = Math.round(rating);
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <StarIcon
          key={i}
          className={`w-4 h-4 ${i < rounded ? "" : "opacity-25"}`}
          style={{ color: "#F5B400" }}
          aria-hidden="true"
        />
      );
    }
    return (
      <span className="gdx-stars inline-flex" role="img" aria-label={`${rounded} / 5`}>
        {stars}
      </span>
    );
  };

  // Computed description body for the current state. We render the
  // server-truncated initial snippet on first paint (matching SSR) and
  // swap to the full text once the user expands.
  const descriptionHtml = showFullContent
    ? fullDescription
    : isDesktop
    ? fullDescriptionDesktop
    : initialDescription;

  return (
    <>
      {/* Rating + location row */}
      <div
        className="flex flex-wrap items-center mb-7 text-[14px]"
        style={{ color: "var(--g-ink-700)", gap: "10px 16px" }}
      >
        {showRating && (
          <button
            type="button"
            onClick={handleScrollToReviews}
            className="inline-flex items-center gap-2.5"
            aria-label={t("jump_to_reviews", { defaultValue: "Voir les avis" }) as string}
          >
            {renderStars(ratingValue)}
            <span style={{ fontWeight: 700 }}>{ratingValue.toFixed(1)}</span>
            {totalReviews > 0 && (
              <span style={{ color: "var(--g-ink-500)", marginLeft: 4 }}>
                ·{" "}
                {totalReviews > 1
                  ? t("reviews", { totalReview: totalReviews })
                  : t("review", { totalReview: totalReviews })}
              </span>
            )}
          </button>
        )}
        {cityName && (
          <>
            <span
              aria-hidden="true"
              style={{ width: 3, height: 3, borderRadius: "50%", background: "var(--g-ink-300)" }}
            />
            <span className="inline-flex items-center gap-2">
              <SvgIcon size={14}>
                <path d="M12 22s-7-7.5-7-13a7 7 0 0 1 14 0c0 5.5-7 13-7 13z" />
                <circle cx="12" cy="9" r="2.5" />
              </SvgIcon>
              <span className="capitalize">{cityName}</span>
            </span>
          </>
        )}
      </div>

      {/* Description */}
      {fullDescription && (
        <>
          <h2 className="font-bold mb-3" style={{ color: "var(--g-burgundy)", fontSize: 22 }}>
            {t("tour_what_you_will_live", { defaultValue: "Ce que vous allez vivre" })}
          </h2>
          <div
            className="tour-content"
            style={{ color: "var(--g-ink-700)", fontSize: 15, lineHeight: 1.65 }}
            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
          />
          <button
            type="button"
            onClick={() => setShowFullContent(!showFullContent)}
            className="mt-3 text-sm font-semibold hover:underline"
            style={{ color: "var(--g-coral)" }}
          >
            {showFullContent ? t("see_less") : t("see_more")}
          </button>
        </>
      )}

      {/* Tout savoir sur l'expérience */}
      <h2
        className="font-bold mt-10 mb-4"
        style={{ color: "var(--g-burgundy)", fontSize: 22 }}
      >
        {t("tour_know_everything", { defaultValue: "Tout savoir sur l'expérience" })}
      </h2>

      <div
        role="tablist"
        aria-label={t("tour_sections", { defaultValue: "Sections du tour" }) as string}
        className="grid gap-2.5 mb-4 grid-cols-2 md:grid-cols-4"
      >
        {tabs.map((tab) => {
          const selected = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              id={`tab-${tab.id}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`tabpanel-${tab.id}`}
              tabIndex={selected ? 0 : -1}
              className={`gdx-tile ${selected ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="gdx-tile__icon">
                <SvgIcon size={18}>{tab.path}</SvgIcon>
              </span>
              <span className="min-w-0 flex-1">
                <span className="gdx-tile__label">{tab.label}</span>
                <span className="gdx-tile__sub">{tab.sub}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div
        key={activeTab}
        role="tabpanel"
        id={`tabpanel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
        className="gdx-card tab-fade-in"
        style={{ padding: 24, background: "white", minHeight: 220 }}
      >
        {activeTab === "itinerary" &&
          tour?.itineraries &&
          tour?.itineraries.length > 0 && <ItinerariesH />}
        {activeTab === "highlights" && (
          <div className="cms-content">
            <CmsEntry data={entryHighlights?.data || []} />
          </div>
        )}
        {activeTab === "included" && (
          <div className="cms-content">
            <CmsEntry data={entryWhatsIncluded?.data || []} />
          </div>
        )}
        {activeTab === "not-included" && (
          <div className="cms-content">
            <CmsEntry data={entryWhatsNotIncluded?.data || []} />
          </div>
        )}
        {activeTab === "important-information" && (
          <div className="cms-content">
            <CmsEntry data={entryImportantInformation?.data || []} />
          </div>
        )}
        {activeTab === "meeting-point" && (
          <div className="cms-content">
            <CmsEntry data={entryMeetingPoint?.data || []} />
          </div>
        )}
        {activeTab === "accessibility" && (
          <div className="cms-content">
            <CmsEntry data={entryAccessibility?.data || []} />
          </div>
        )}
        {activeTab === "cancellation" && (
          <div className="cms-content">
            <CmsEntry data={entryCancellation?.data || []} />
          </div>
        )}
      </div>

      {/* Reviews — deferred until scrolled near */}
      <div ref={reviewsRef} className="mt-10" style={{ minHeight: 200 }}>
        {reviewsMounted && <Reviews />}
      </div>
    </>
  );
};

export default TourLeftBody;
